// web/src/sentry.js
import * as Sentry from '@sentry/react';

// Console breadcrumbs (and the session replays built from them) must never carry credentials.
const SENSITIVE_CONSOLE_PATTERN = /password|passwd|token|otp|secret|authorization/i;

// JSON-serialise breadcrumb data without throwing on circular references or BigInt, and without
// walking DOM nodes or window (React hangs its whole fiber tree off DOM nodes).
// Returns null when the data cannot be serialised at all.
function serialiseBreadcrumbData(data) {
  const seen = new WeakSet();
  try {
    return JSON.stringify(data, (key, value) => {
      if (typeof value === 'bigint') return value.toString();
      if (typeof value !== 'object' || value === null) return value;
      if (typeof window !== 'undefined' && value === window) return '[Window]';
      if (typeof Node !== 'undefined' && value instanceof Node) return `[${value.nodeName}]`;
      if (seen.has(value)) return '[Circular]';
      seen.add(value);
      // Error fields are non-enumerable, so JSON.stringify alone would emit {} for them.
      if (value instanceof Error) return { name: value.name, message: value.message, ...value };
      return value;
    }) ?? '';
  } catch {
    return null;
  }
}

// Drops console breadcrumbs whose message or serialised data mentions a password, token, OTP,
// secret or Authorization header. Other breadcrumbs pass through.
export function filterSensitiveConsoleBreadcrumb(breadcrumb) {
  if (!breadcrumb || breadcrumb.category !== 'console') return breadcrumb;
  if (SENSITIVE_CONSOLE_PATTERN.test(String(breadcrumb.message ?? ''))) return null;
  if (breadcrumb.data === undefined) return breadcrumb;
  const serialised = serialiseBreadcrumbData(breadcrumb.data);
  // Data that cannot be serialised cannot be checked, so it is dropped rather than risked.
  if (serialised === null || SENSITIVE_CONSOLE_PATTERN.test(serialised)) return null;
  return breadcrumb;
}

// URLs whose path carries a secret: password-reset links (/reset-password/:token, App.jsx),
// vouch invites (page /vouch/:token, API GET /vouch/token/:token).
const SECRET_PATH_PATTERNS = [
  /(\/reset-password\/)[^/?#\s]+/gi,
  /(\/vouch\/token\/)[^/?#\s]+/gi,
  // The page route only (path starts with /vouch/); API paths such as /api/vouch/my-status are not secrets.
  /^((?:[a-z][a-z\d+.-]*:\/\/[^/?#\s]*)?\/vouch\/)(?!token(?:[/?#]|$))[^/?#\s]+/i,
];
// Query/fragment parameters named like a credential, e.g. /oauth/callback?token=<JWT>.
const SECRET_PARAM_PATTERN = /([?&#][^=&#\s]*(?:password|passwd|token|otp|secret|authorization)[^=&#\s]*=)[^&#\s]*/gi;

// Replaces the secret segment of those routes with ':token' and credential-named query values
// with '[Filtered]'. Accepts full URLs, paths, transaction names and bare query strings.
export function scrubSensitiveUrl(value) {
  if (typeof value !== 'string') return value;
  let scrubbed = value;
  for (const pattern of SECRET_PATH_PATTERNS) scrubbed = scrubbed.replace(pattern, '$1:token');
  return scrubbed.replace(SECRET_PARAM_PATTERN, '$1[Filtered]');
}

const BREADCRUMB_URL_FIELDS = ['from', 'to', 'url'];

// Sentry `beforeBreadcrumb` hook: the console filter above, then any breadcrumb's data.from /
// data.to / data.url (navigation, xhr, fetch) has its secret URL segments scrubbed. Session
// replays receive breadcrumbs after this hook, so they get the scrubbed version.
export function scrubBreadcrumb(breadcrumb) {
  const kept = filterSensitiveConsoleBreadcrumb(breadcrumb);
  if (!kept || !kept.data || typeof kept.data !== 'object') return kept;
  let data = null;
  for (const field of BREADCRUMB_URL_FIELDS) {
    const original = kept.data[field];
    const scrubbed = scrubSensitiveUrl(original);
    if (scrubbed !== original) {
      data = data || { ...kept.data };
      data[field] = scrubbed;
    }
  }
  return data ? { ...kept, data } : kept;
}

const SPAN_URL_FIELDS = ['url', 'http.url', 'http.query', 'url.full'];

function scrubFields(target, fields) {
  if (!target || typeof target !== 'object') return;
  for (const field of fields) {
    if (typeof target[field] === 'string') target[field] = scrubSensitiveUrl(target[field]);
  }
}

// Sentry `beforeSend` / `beforeSendTransaction` hook (and replay-event processor): parametrises
// pageload/navigation transaction names (/reset-password/abc → /reset-password/:token) and scrubs
// the same secrets from the page URL, Referer, span URLs and a replay's list of visited URLs.
export function scrubEventUrls(event) {
  if (!event || typeof event !== 'object') return event;
  if (typeof event.transaction === 'string') {
    const name = scrubSensitiveUrl(event.transaction);
    if (name !== event.transaction) {
      event.transaction = name;
      if (event.transaction_info?.source === 'url') {
        event.transaction_info = { ...event.transaction_info, source: 'route' };
      }
    }
  }
  if (event.request) {
    scrubFields(event.request, ['url']);
    scrubFields(event.request.headers, ['Referer', 'referer']);
  }
  scrubFields(event.contexts?.trace?.data, SPAN_URL_FIELDS);
  if (Array.isArray(event.spans)) {
    for (const span of event.spans) {
      scrubFields(span, ['description']);
      scrubFields(span?.data, SPAN_URL_FIELDS);
    }
  }
  if (Array.isArray(event.urls)) event.urls = event.urls.map(scrubSensitiveUrl);
  return event;
}

// beforeSend does not run for session-replay events, so their URL list is scrubbed by an event processor.
export function scrubReplayEventUrls(event) {
  return event?.type === 'replay_event' ? scrubEventUrls(event) : event;
}

export function initSentry() {
  if (import.meta.env.VITE_SENTRY_DSN_WEB) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN_WEB,
      environment: import.meta.env.MODE,
      release: `gigship-web@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Record 10% of sessions, 100% with errors
          maskAllText: true,         // privacy: mask all text in replays
          blockAllMedia: true,       // privacy: block all media
        }),
      ],
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      beforeBreadcrumb: scrubBreadcrumb,
      beforeSend: scrubEventUrls,
      beforeSendTransaction: scrubEventUrls,
      // Don't capture errors from browser extensions
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
      ],
    });
    Sentry.addEventProcessor(scrubReplayEventUrls);
    console.log('[Sentry] Web monitoring initialized successfully');
  } else {
    console.warn('[Sentry] VITE_SENTRY_DSN_WEB is missing, skipping initialization');
  }
}

export { Sentry };